import React, { useEffect, useState, useMemo } from 'react';
import { useScheduleActionLock } from '../../hook/useScheduleActionLock';
import { useNavigate } from 'react-router-dom';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body2Regular, CaptionLight } from '../../lib/font';
import Modal from '../modal/Modal';
import ColorSquareButton from '../Button/ColorSquareButton';
import WhiteSquareButton from '../Button/WhiteSquareButton';
import GroupCategoryField from './GroupCategoryField';
import { useCreateTemplate } from '@/features/groupSession/hooks';
import { useTickets } from '@/features/groupTicket/hooks';
import { ClassType, ClassCategory, CreateTemplateBody } from '@/features/groupSession/types';
import dayjs from 'dayjs';

const CLASS_TYPE_OPTIONS: { code: ClassType; label: string; capacity: number | null; defaultTitle: string; defaultCategory: string }[] = [
    { code: 'GROUP', label: '그룹 수업', capacity: null, defaultTitle: 'GX 오전반', defaultCategory: 'GX' },
    { code: 'PRIVATE_1ON2', label: '프라이빗 1:2', capacity: 2, defaultTitle: '프라이빗 수업', defaultCategory: 'PT' },
    { code: 'PRIVATE_1ON3', label: '프라이빗 1:3', capacity: 3, defaultTitle: '프라이빗 수업', defaultCategory: 'PT' },
];

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const inputStyle: React.CSSProperties = {
    border: '1px solid #E1E4E6', borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none',
};

const GroupSessionCreateModal = ({
    onClose,
    createAlert,
    scheduleCreateLock = false,
}: {
    onClose: () => void;
    createAlert: any;
    /**
     * 스케줄 화면의 생성 흐름에서 열 때만 true — 저장 직전에 「수업 생성」 잠금을 다시 본다.
     * 수강권 탭(수업 정의 관리)에서 열면 그 탭의 잠금을 따르므로 false(무변경). 설계 §6-8.
     */
    scheduleCreateLock?: boolean;
}) => {
    const scheduleLock = useScheduleActionLock('schedule.create');
    const navigate = useNavigate();
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId = authObject?.centerId;

    const [step, setStep] = useState<1 | 2>(1);

    // ── Step 1: 기본 정보 ──
    const [classType, setClassType] = useState<ClassType>('GROUP');
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState('GX');
    // '직접 입력'을 고른 상태 — 수업 유형을 바꿔도 입력값을 덮어쓰지 않는다
    const [isCustomCategory, setIsCustomCategory] = useState(false);
    const [allowedTicketIds, setAllowedTicketIds] = useState<string[]>([]);
    const [ticketSearch, setTicketSearch] = useState('');
    const [isRecurring, setIsRecurring] = useState(false);
    const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs().add(30, 'day').format('YYYY-MM-DD'));
    const [weekdays, setWeekdays] = useState<{ day: number; startTime: string; endTime: string }[]>([]);

    // ── Step 2: 세부 설정 ──
    const [maxCapacity, setMaxCapacity] = useState(6);
    const [minCapacity, setMinCapacity] = useState(0);
    const [waitlistLimit, setWaitlistLimit] = useState(0);
    const [roomId, setRoomId] = useState('');
    const [memo, setMemo] = useState('');
    const [useCustomRules, setUseCustomRules] = useState(false);
    const [bookingOpenDeadline, setBookingOpenDeadline] = useState(7); // 일
    const [cancellationDeadline, setCancellationDeadline] = useState(8); // 시간
    const [closingDeadline, setClosingDeadline] = useState(8); // 시간
    const [waitlistAutoDeadline, setWaitlistAutoDeadline] = useState(1); // 시간

    // 데이터 훅
    const { data: tickets = [] } = useTickets({ centerId, enabled: !!centerId });

    // 선택한 수업 유형에 대응하는 수강권만 노출
    const compatibleTickets = useMemo(() => {
        return (tickets as any[]).filter((t) => {
            const allowed: string[] = Array.isArray(t.allowedClassTypes) ? t.allowedClassTypes : [];
            if (allowed.length > 0) return allowed.includes(classType);
            return t.classType === classType;
        });
    }, [tickets, classType]);

    // 수업 유형 변경 시 더 이상 호환되지 않는 수강권 자동 해제
    useEffect(() => {
        setAllowedTicketIds((prev) => prev.filter((id) => compatibleTickets.some((t) => t._id === id)));
    }, [compatibleTickets]);

    const createTemplateMutation = useCreateTemplate();

    const selectedClassOpt = CLASS_TYPE_OPTIONS.find((o) => o.code === classType);
    const fixedCapacity = selectedClassOpt?.capacity;

    const toggleTicket = (ticketId: string) => {
        setAllowedTicketIds((prev) =>
            prev.includes(ticketId) ? prev.filter((id) => id !== ticketId) : [...prev, ticketId]
        );
    };

    const toggleWeekday = (day: number) => {
        setWeekdays((prev) => {
            const exists = prev.find((w) => w.day === day);
            if (exists) return prev.filter((w) => w.day !== day);
            return [...prev, { day, startTime: '10:00', endTime: '10:50' }].sort((a, b) => a.day - b.day);
        });
    };

    const updateWeekdayTime = (day: number, field: 'startTime' | 'endTime', value: string) => {
        setWeekdays((prev) => prev.map((w) => w.day === day ? { ...w, [field]: value } : w));
    };

    // 반복 수업 시 예상 세션 수 계산
    const estimatedSessions = useMemo(() => {
        if (!isRecurring || weekdays.length === 0) return 0;
        let count = 0;
        let current = dayjs(startDate);
        const end = dayjs(endDate);
        while (current.isBefore(end) || current.isSame(end, 'day')) {
            if (weekdays.some((w) => w.day === current.day())) count++;
            current = current.add(1, 'day');
        }
        return count;
    }, [isRecurring, startDate, endDate, weekdays]);

    const validateStep1 = (): boolean => {
        if (!title.trim()) { createAlert('입력 오류', '수업명을 입력해주세요.', true); return false; }
        if (!category.trim()) { createAlert('입력 오류', '카테고리를 입력해주세요.', true); return false; }
        if (allowedTicketIds.length === 0) { createAlert('입력 오류', '사용 가능 수강권을 선택해주세요.', true); return false; }
        return true;
    };

    const handleSubmit = async () => {
        const actualMaxCapacity = fixedCapacity ?? maxCapacity;

        const body: CreateTemplateBody & Record<string, any> = {
            title: title.trim(),
            classType,
            category: category.trim() as ClassCategory,
            allowedTicketIds,
            maxCapacity: actualMaxCapacity,
            minCapacity: fixedCapacity ? 0 : minCapacity,
            waitlistLimit,
        };
        if (roomId) body.roomId = roomId;
        if (memo) body.memo = memo;
        if (useCustomRules) {
            body.bookingOpenDeadline = bookingOpenDeadline * 1440;
            body.cancellationDeadline = cancellationDeadline * 60;
            body.closingDeadline = closingDeadline * 60;
            body.waitlistAutoDeadline = waitlistAutoDeadline * 60;
        }

        if (scheduleCreateLock && scheduleLock.requiresCheck() && !(await scheduleLock.ensure())) return;
        try {
            await createTemplateMutation.mutateAsync({ centerId, body });
            createAlert('등록 완료', `"${title.trim()}" 수업이 생성되었습니다.`, false);
            onClose();
        } catch (err: any) {
            createAlert('등록 실패', err?.serverMessage || '수업 등록에 실패했습니다.', true);
        }
    };

    const isSubmitting = createTemplateMutation.isLoading;

    return (
        <Modal isCloseButton setIsVisible={onClose} whiteViewWidth={540}>
            <Col style={{ padding: '28px', overflow: 'auto', maxHeight: '85vh' }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                    수업 생성 ({step}/2)
                </span>
                <CaptionLight color={colors.LAVEL_1} style={{ marginBottom: 20 }}>
                    {step === 1 ? '수업 정보를 설정합니다' : '정원, 장소, 규칙을 설정합니다'}
                </CaptionLight>

                {/* ═══════ Step 1 ═══════ */}
                {step === 1 && (
                    <Col style={{ gap: 16 }}>
                        {/* 수업 유형 */}
                        <Col style={{ gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>수업 유형</Body2Regular>
                            <Row style={{ gap: 6 }}>
                                {CLASS_TYPE_OPTIONS.map((opt) => {
                                    const sel = classType === opt.code;
                                    return (
                                        <button key={opt.code} onClick={() => {
                                            setClassType(opt.code);
                                            if (!title || CLASS_TYPE_OPTIONS.some(o => o.defaultTitle === title)) {
                                                setTitle(opt.defaultTitle);
                                            }
                                            if (!isCustomCategory && CLASS_TYPE_OPTIONS.some(o => o.defaultCategory === category)) {
                                                setCategory(opt.defaultCategory);
                                            }
                                        }} style={{
                                            flex: 1, padding: '10px 0', borderRadius: 8,
                                            border: sel ? '1.5px solid #10B981' : '1px solid #E1E4E6',
                                            backgroundColor: sel ? '#ECFDF5' : 'white',
                                            color: sel ? '#065F46' : '#6B7280',
                                            fontSize: 13, fontWeight: sel ? 600 : 400, cursor: 'pointer',
                                        }}>{opt.label}</button>
                                    );
                                })}
                            </Row>
                        </Col>

                        {/* 수업명 / 카테고리 */}
                        <Col style={{ gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>수업명</Body2Regular>
                            <input style={inputStyle} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={CLASS_TYPE_OPTIONS.find(o => o.code === classType)?.defaultTitle || 'GX 오전반'} />
                        </Col>
                        <Col style={{ gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>카테고리</Body2Regular>
                            <GroupCategoryField
                                value={category}
                                isCustom={isCustomCategory}
                                onChange={({ value, isCustom }) => { setCategory(value); setIsCustomCategory(isCustom); }}
                                selectStyle={inputStyle}
                                inputStyle={inputStyle}
                            />
                        </Col>

                        {/* 수강권 선택 */}
                        <Col style={{ gap: 6 }}>
                            <Row style={{ alignItems: 'center', gap: 6 }}>
                                <Body2Regular color={colors.LAVEL_2}>사용 가능 수강권</Body2Regular>
                                <span style={{ fontSize: 11, color: '#EF4444', fontWeight: 600 }}>필수</span>
                                {allowedTicketIds.length > 0 && (
                                    <span style={{ fontSize: 11, color: '#10B981', fontWeight: 600, marginLeft: 'auto' }}>
                                        {allowedTicketIds.length}개 선택됨
                                    </span>
                                )}
                            </Row>
                            <CaptionLight color={colors.LAVEL_1}>선택한 수강권을 보유한 회원에게만 수업이 노출됩니다</CaptionLight>
                            {allowedTicketIds.length === 0 && compatibleTickets.length > 0 && (
                                <span style={{ fontSize: 12, color: '#F59E0B', fontWeight: 500 }}>수강권을 1개 이상 선택해야 다음 단계로 진행할 수 있습니다</span>
                            )}

                            {/* 검색 */}
                            {compatibleTickets.length > 6 && (
                                <input
                                    value={ticketSearch}
                                    onChange={(e) => setTicketSearch(e.target.value)}
                                    placeholder="수강권 이름으로 검색..."
                                    style={{ ...inputStyle, fontSize: 13, padding: '7px 12px' }}
                                />
                            )}

                            {/* 전체선택/해제 */}
                            {compatibleTickets.length > 3 && (
                                <Row style={{ gap: 8, marginTop: 2 }}>
                                    <button onClick={() => setAllowedTicketIds(compatibleTickets.map((t) => t._id))}
                                        style={{ fontSize: 11, color: '#3B82F6', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                        전체 선택
                                    </button>
                                    <button onClick={() => setAllowedTicketIds([])}
                                        style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                        전체 해제
                                    </button>
                                </Row>
                            )}

                            <Col style={{ gap: 6, maxHeight: 180, overflowY: 'auto', marginTop: 4 }}>
                                <Row style={{ gap: 6, flexWrap: 'wrap' }}>
                                    {compatibleTickets
                                        .filter((t) => !ticketSearch || t.name.toLowerCase().includes(ticketSearch.toLowerCase()))
                                        .map((t) => {
                                            const sel = allowedTicketIds.includes(t._id);
                                            return (
                                                <button key={t._id} onClick={() => toggleTicket(t._id)} style={{
                                                    padding: '6px 14px', borderRadius: 6,
                                                    border: sel ? '1.5px solid #10B981' : '1px solid #E1E4E6',
                                                    backgroundColor: sel ? '#ECFDF5' : 'white',
                                                    color: sel ? '#065F46' : '#6B7280',
                                                    fontSize: 12, fontWeight: sel ? 600 : 400, cursor: 'pointer',
                                                }}>{sel ? '✓ ' : ''}{t.name}</button>
                                            );
                                        })}
                                    {compatibleTickets.length === 0 && (
                                        <Col style={{ width: '100%', padding: '14px 16px', gap: 8, backgroundColor: '#FFFBEB', border: '1px dashed #FCD34D', borderRadius: 8 }}>
                                            <span style={{ fontSize: 13, fontWeight: 600, color: '#92400E' }}>
                                                {(tickets as any[]).length === 0
                                                    ? '등록된 수강권이 없습니다'
                                                    : `'${CLASS_TYPE_OPTIONS.find((o) => o.code === classType)?.label}'에 사용 가능한 수강권이 없습니다`}
                                            </span>
                                            <span style={{ fontSize: 12, color: '#B45309', lineHeight: 1.6 }}>
                                                <b>수강권 관리 &gt; 그룹수업 수강권</b> 탭에서{' '}
                                                <b>+ 수강권 등록</b> 버튼을 눌러{' '}
                                                <b>{CLASS_TYPE_OPTIONS.find((o) => o.code === classType)?.label}</b>을(를) 지원하는 수강권 상품을 먼저 만들어주세요.
                                            </span>
                                            <button
                                                onClick={() => {
                                                    onClose();
                                                    navigate('/classPass?tab=group&sub=tickets');
                                                }}
                                                style={{
                                                    alignSelf: 'flex-start',
                                                    marginTop: 4,
                                                    padding: '7px 14px',
                                                    borderRadius: 8,
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    backgroundColor: '#F59E0B',
                                                    color: '#FFFFFF',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                수강권 등록하러 가기 →
                                            </button>
                                        </Col>
                                    )}
                                    {compatibleTickets.length > 0 && ticketSearch && compatibleTickets.filter((t) => t.name.toLowerCase().includes(ticketSearch.toLowerCase())).length === 0 && (
                                        <span style={{ fontSize: 13, color: '#9CA3AF' }}>'{ticketSearch}' 검색 결과가 없습니다</span>
                                    )}
                                </Row>
                            </Col>
                        </Col>

                    </Col>
                )}

                {/* ═══════ Step 2 ═══════ */}
                {step === 2 && (
                    <Col style={{ gap: 16 }}>
                        {/* 정원 - 자연어 UI */}
                        <Col style={{ gap: 10, padding: 16, backgroundColor: '#F9FAFB', borderRadius: 10, border: '1px solid #E5E7EB' }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 2 }}>인원 설정</span>

                            {fixedCapacity ? (
                                <p style={{ fontSize: 14, color: '#374151', lineHeight: 2, margin: 0 }}>
                                    정원은 <strong>{fixedCapacity}명</strong>으로 고정됩니다.
                                </p>
                            ) : (
                                <p style={{ fontSize: 14, color: '#374151', lineHeight: 2.2, margin: 0 }}>
                                    최대{' '}
                                    <input
                                        type="number" value={maxCapacity} min={1}
                                        onChange={(e) => setMaxCapacity(Number(e.target.value))}
                                        style={{ width: 56, border: '1.5px solid #93C5FD', borderRadius: 6, padding: '2px 8px', fontSize: 14, fontWeight: 700, color: '#1E40AF', textAlign: 'center', outline: 'none', backgroundColor: '#EFF6FF' }}
                                    />
                                    {' '}명이고, 최소{' '}
                                    <input
                                        type="number" value={minCapacity} min={0}
                                        onChange={(e) => setMinCapacity(Number(e.target.value))}
                                        style={{ width: 56, border: '1.5px solid #93C5FD', borderRadius: 6, padding: '2px 8px', fontSize: 14, fontWeight: 700, color: '#1E40AF', textAlign: 'center', outline: 'none', backgroundColor: '#EFF6FF' }}
                                    />
                                    {' '}명이 차면 진행되고, 안 차면 폐강될 수 있습니다.
                                    {minCapacity === 0 && (
                                        <span style={{ fontSize: 12, color: '#9CA3AF' }}> (최소 인원 0 = 폐강 없음)</span>
                                    )}
                                </p>
                            )}

                            <p style={{ fontSize: 14, color: '#374151', lineHeight: 2.2, margin: 0 }}>
                                대기 인원은 최대{' '}
                                <input
                                    type="number" value={waitlistLimit} min={0}
                                    onChange={(e) => setWaitlistLimit(Number(e.target.value))}
                                    style={{ width: 56, border: '1.5px solid #93C5FD', borderRadius: 6, padding: '2px 8px', fontSize: 14, fontWeight: 700, color: '#1E40AF', textAlign: 'center', outline: 'none', backgroundColor: '#EFF6FF' }}
                                />
                                {' '}명까지 허용하고, 예약 인원이 빠지면 자동으로 배정됩니다.
                                {waitlistLimit === 0 && (
                                    <span style={{ fontSize: 12, color: '#9CA3AF' }}> (0 = 대기 비활성)</span>
                                )}
                            </p>
                        </Col>

                        {/* 장소 */}
                        <Col style={{ gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>장소 <span style={{ fontSize: 12, color: '#9CA3AF' }}>(선택)</span></Body2Regular>
                            <input style={inputStyle} value={roomId} onChange={(e) => setRoomId(e.target.value)} placeholder="3층 GX룸" />
                        </Col>

                        {/* 메모 */}
                        <Col style={{ gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>메모 <span style={{ fontSize: 12, color: '#9CA3AF' }}>(선택)</span></Body2Regular>
                            <textarea value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="수업 관련 메모"
                                style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} />
                        </Col>

                        {/* 커스텀 규칙 */}
                        <Col style={{ gap: 8, padding: 16, backgroundColor: '#F9FAFB', borderRadius: 10, border: '1px solid #E5E7EB' }}>
                            <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>예약 규칙 커스텀</span>
                                <button onClick={() => setUseCustomRules(!useCustomRules)}
                                    style={{
                                        width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                                        backgroundColor: useCustomRules ? '#3B82F6' : '#D1D5DB', position: 'relative', transition: 'background-color 0.2s',
                                    }}>
                                    <div style={{
                                        width: 20, height: 20, borderRadius: '50%', backgroundColor: 'white',
                                        position: 'absolute', top: 3, left: useCustomRules ? 25 : 3, transition: 'left 0.2s',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                    }} />
                                </button>
                            </Row>
                            {!useCustomRules && (
                                <span style={{ fontSize: 12, color: '#9CA3AF' }}>센터 기본 설정을 사용합니다</span>
                            )}

                            {useCustomRules && (
                                <Col style={{ gap: 8, marginTop: 4 }}>
                                    <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: 13, color: '#374151' }}>예약 오픈</span>
                                        <Row style={{ alignItems: 'center', gap: 6 }}>
                                            <input type="number" value={bookingOpenDeadline}
                                                onChange={(e) => setBookingOpenDeadline(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
                                                min={0} step={1} style={{ ...inputStyle, width: 70, textAlign: 'right', fontSize: 13 }} />
                                            <span style={{ fontSize: 13, color: '#6B7280' }}>일 전</span>
                                        </Row>
                                    </Row>
                                    <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: 13, color: '#374151' }}>취소 마감</span>
                                        <Row style={{ alignItems: 'center', gap: 6 }}>
                                            <input type="number" value={cancellationDeadline} onChange={(e) => setCancellationDeadline(Number(e.target.value))}
                                                min={0} style={{ ...inputStyle, width: 70, textAlign: 'right', fontSize: 13 }} />
                                            <span style={{ fontSize: 13, color: '#6B7280' }}>시간 전</span>
                                        </Row>
                                    </Row>
                                    <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: 13, color: '#374151' }}>폐강 기준</span>
                                        <Row style={{ alignItems: 'center', gap: 6 }}>
                                            <input type="number" value={closingDeadline} onChange={(e) => setClosingDeadline(Number(e.target.value))}
                                                min={0} style={{ ...inputStyle, width: 70, textAlign: 'right', fontSize: 13 }} />
                                            <span style={{ fontSize: 13, color: '#6B7280' }}>시간 전</span>
                                        </Row>
                                    </Row>
                                    <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: 13, color: '#374151' }}>대기 자동 확정</span>
                                        <Row style={{ alignItems: 'center', gap: 6 }}>
                                            <input type="number" value={waitlistAutoDeadline} onChange={(e) => setWaitlistAutoDeadline(Number(e.target.value))}
                                                min={0} style={{ ...inputStyle, width: 70, textAlign: 'right', fontSize: 13 }} />
                                            <span style={{ fontSize: 13, color: '#6B7280' }}>시간 전</span>
                                        </Row>
                                    </Row>
                                </Col>
                            )}
                        </Col>

                        {/* 요약 */}
                        <Col style={{ padding: 16, backgroundColor: '#EFF6FF', borderRadius: 10, border: '1px solid #BFDBFE', gap: 4 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1E40AF' }}>생성 요약</span>
                            <span style={{ fontSize: 13, color: '#1E40AF' }}>
                                {title || '(수업명 미입력)'} · {CLASS_TYPE_OPTIONS.find((o) => o.code === classType)?.label}
                                {' · '}정원 {fixedCapacity ?? maxCapacity}명
                            </span>
                        </Col>
                    </Col>
                )}

                {/* 버튼 */}
                <Row style={{ gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
                    {step === 1 && (
                        <>
                            <WhiteSquareButton text="취소" size="small" callBack={onClose} />
                            <ColorSquareButton text="다음 →" size="small"
                                callBack={() => { if (validateStep1()) setStep(2); }}
                                isDisabled={!title.trim() || allowedTicketIds.length === 0} />
                        </>
                    )}
                    {step === 2 && (
                        <>
                            <WhiteSquareButton text="← 이전" size="small" callBack={() => setStep(1)} />
                            <ColorSquareButton text="생성" size="small" callBack={handleSubmit} isDisabled={isSubmitting} />
                        </>
                    )}
                </Row>
            </Col>
            {scheduleCreateLock && scheduleLock.gate}
        </Modal>
    );
};

export default GroupSessionCreateModal;
